import React, { useState } from 'react';
import { analyzeComments } from '../services/api';
import { cacheAnalysisResult, getCachedResult, CACHE_TYPES } from '../services/cache';
import TikTokEmbed from './TikTokEmbed';
import SearchLimitBanner from './SearchLimitBanner';
import { hasReachedLimit, incrementSearchCount } from '../services/searchLimit';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

function CommentAnalysis() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);

  // Helper function to extract username and video ID from URL
  const extractInfoFromUrl = (url) => {
    // Regex to capture username and video ID
    const match = url.match(/tiktok\.com\/@([^/]+)\/video\/(\d+)/); 
    if (match && match[1] && match[2]) {
      return { username: match[1], videoId: match[2] };
    }
    return null;
  };

  const handleAnalyze = async () => {
    console.log("handleAnalyze triggered"); // Log entry
    if (!url) {
      console.log("Exiting handleAnalyze: URL is empty");
      setError('Please enter a TikTok video URL');
      return;
    }
    console.log("URL check passed");

    if (!url.includes('tiktok.com')) {
      console.log("Exiting handleAnalyze: URL does not include tiktok.com");
      setError('Please enter a valid TikTok video URL');
      return;
    }
    console.log("tiktok.com check passed");

    if (hasReachedLimit()) {
      console.log("Exiting handleAnalyze: Search limit reached");
      setError('You have reached your daily search limit');
      return;
    }
    console.log("Search limit check passed");

    // Clear previous analysis when starting a new search
    setAnalysis(null);
    console.log("Cleared previous analysis");

    try {
      // Check cache first
      console.log("Checking cache...");
      const cached = getCachedResult(CACHE_TYPES.COMMENT);
      if (cached?.data?.url === url) {
        console.log('Using cached analysis for:', url);
        setAnalysis(cached.data);
        return; // Exit if using cache
      }
      console.log("Cache check passed (no match or no cache)");

      setLoading(true);
      setError('');
      
      console.log("Extracting info from URL:", url);
      const extractedInfo = extractInfoFromUrl(url);
      if (!extractedInfo) {
        console.log("Exiting handleAnalyze: Failed to extract info from URL");
        setError('Could not extract username and video ID from the URL. Please use a valid TikTok video URL (e.g., https://www.tiktok.com/@username/video/123...).');
        setLoading(false); // Ensure loading is set to false here
        return;
      }
      console.log("URL extraction passed");

      const { username, videoId } = extractedInfo;
      console.log('Extracted username:', username, 'Video ID:', videoId); 
      
      console.log("Attempting to call analyzeComments API..."); // Log before API call
      // Pass both username and videoId to the API service
      const data = await analyzeComments(username, videoId); 
      console.log("analyzeComments API call successful, data:", data); // Log after successful API call
      setAnalysis(data);
      incrementSearchCount();

      // Cache the new results with the videoId (or full URL if needed for cache key)
      cacheAnalysisResult(CACHE_TYPES.COMMENT, '', { ...data, videoId, username, url }); // Cache relevant info
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to analyze comments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <SearchLimitBanner />
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderRadius: 2,
        p: 3
      }}>
        <Avatar sx={{ 
          width: 56, 
          height: 56,
          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
        }}>
          <CommentIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Comment Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analyze sentiment and engagement patterns in your video comments
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 3,
        mb: 3,
        borderRadius: 2,
        bgcolor: 'rgba(139, 92, 246, 0.05)',
        border: '1px solid rgba(139, 92, 246, 0.1)'
      }}>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
          To use Comment Analysis:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
          1. Copy the TikTok video link{'\n'}
          2. Paste into your browser (Safari, Chrome, etc.){'\n'}
          3. Copy browser link{'\n'}
          4. Paste it here
        </Typography>
      </Paper>

      <Card sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, 
        mb: 3,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
        borderRadius: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <CommentIcon sx={{ 
            fontSize: 28,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} />
          <Typography variant="h5" component="h2" sx={{ 
            fontWeight: 600,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Analyze Video Comments
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', mb: 3 }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
              fullWidth
              label="TikTok Video URL"
              placeholder="https://www.tiktok.com/@username/video/1234567890"
              variant="outlined"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                  }
                }
              }}
            />
            <Tooltip
              title="Enter the URL of any TikTok video to analyze its comments and get AI-powered insights"
              placement="top-start"
              arrow
            >
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 12 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: 0.6,
                  '&:hover': { opacity: 1 }
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleAnalyze}
          disabled={loading}
          fullWidth
          sx={{
            height: 48,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
            },
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommentIcon />
            <span>Analyze Comments</span>
          </Box>
        </Button>

        {url && <TikTokEmbed url={url} />}

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {analysis && (
          <Paper sx={{ 
            mt: 3, 
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
          }}>
            {/* --- Corrected Rendering Logic --- */}

            {/* 1. Show Sentiment Bars if scores exist */}
            {analysis.analysis?.positiveScore !== undefined && ( 
              <Box mb={3}> {/* Wrap in Box for margin */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <SmartToyIcon sx={{ // Use SmartToyIcon for consistency
                    fontSize: 24, 
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }} /> {/* Close Icon Tag */}
                  <Typography variant="h6" sx={{ // Typography is a sibling
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    AI-Powered Sentiment Analysis
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SentimentSatisfiedAltIcon sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography variant="body1">Positive Sentiment</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analysis.analysis.positiveScore * 100} // Access via analysis.analysis
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#4caf50',
                        borderRadius: 5,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    {Math.round(analysis.analysis.positiveScore * 100)}% 
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SentimentVeryDissatisfiedIcon sx={{ mr: 1, color: '#f44336' }} />
                    <Typography variant="body1">Negative Sentiment</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analysis.analysis.negativeScore * 100} // Access via analysis.analysis
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#f44336',
                        borderRadius: 5,
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    {Math.round(analysis.analysis.negativeScore * 100)}%
                  </Typography>
                </Box>
              </Box> 
            )}

            {/* 2. Show AI Summary Text if it exists */}
            {analysis.analysis?.summary && (
              <Box sx={{ mt: 3 }}> 
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                   <SmartToyIcon sx={{ 
                    fontSize: 24, 
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }} /> {/* Close Icon Tag */}
                  <Typography variant="h6" sx={{ // Typography is a sibling
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    AI Analysis Summary
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                  {analysis.analysis.summary} {/* Access via analysis.analysis */}
                </Typography>
              </Box>
            )}

            {/* 3. Show top comments if they exist */}
            {analysis.topComments && analysis.topComments.length > 0 && (
              <Box sx={{ mt: 3 }}> 
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CommentIcon sx={{ 
                    fontSize: 24, 
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }} /> {/* Close Icon Tag */}
                  <Typography variant="h6" sx={{ // Typography is a sibling
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Most Engaging Comments
                  </Typography>
                </Box>
                {analysis.topComments.map((comment, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    • {comment.text} ({comment.diggCount} likes)
                  </Typography>
                ))}
              </Box>
            )}

            {/* REMOVED incorrect fallback alert */}
            {/* REMOVED recommendations section as it's not populated by backend yet */}
          </Paper>
        )}
      </Card>
    </Box>
  );
}

export default CommentAnalysis;
